import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Alert, Button, Form, Modal, Nav, Spinner, Table } from '@/components/ui';
import GoogleGPSMap from '../../../components/GoogleGPSMap';
import { workIdService } from '../../../services/workIdService';
import type {
  MergeSuggestionsResponse,
  MergeSuggestionCandidate,
  WorkIdAttachment,
} from '../../../services/workIdService';
import type { WorkIdRecord } from '../workIdService';
import WorkIdImageAttachments from './WorkIdImageAttachments';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';
import { confirmAction } from '@/components/ui';

interface WorkIdMergeSuggestionsModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdRecord | null;
  onSuccess: () => void;
}

function formatDate(iso: string): string {
  return formatPacificDate(iso) || iso;
}

function formatReportedBy(rb: { name: string | null; lastName: string | null } | null | undefined): string {
  if (!rb) return '–';
  return [rb.name, rb.lastName].filter(Boolean).join(' ') || '–';
}

function haversineMetres(
  lat1: number, lon1: number, lat2: number, lon2: number
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(metres: number): string {
  if (metres >= 1000) return `~${(metres / 1000).toFixed(1)} km apart`;
  return `~${Math.round(metres)} m apart`;
}

function buildSummaryLine(c: MergeSuggestionCandidate): string {
  const parts: string[] = [];
  if (c.foremanAreaName) parts.push(c.foremanAreaName);
  if (parts.length > 0) return parts.join(' · ');
  return c.summary || '';
}


/* ── Circular match ring (SVG) ────────────────────────────────────── */

const MatchRing: React.FC<{ pct: number; size?: number }> = ({ pct, size = 56 }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const color = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="44%" textAnchor="middle" dominantBaseline="central"
        fontSize="14" fontWeight="700" fill="#333">{pct}%</text>
      <text x="50%" y="66%" textAnchor="middle" dominantBaseline="central"
        fontSize="8" fill="#999">match</text>
    </svg>
  );
};

/* ── Status badge ─────────────────────────────────────────────────── */

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  APPROVED: { bg: '#22c55e', text: '#fff' },
  Approved: { bg: '#22c55e', text: '#fff' },
  NEW: { bg: '#3b82f6', text: '#fff' },
  New: { bg: '#3b82f6', text: '#fff' },
  ASSIGNED: { bg: '#a855f7', text: '#fff' },
  Assigned: { bg: '#a855f7', text: '#fff' },
  MERGED: { bg: '#6b7280', text: '#fff' },
  Merged: { bg: '#6b7280', text: '#fff' },
};

const StatusBadge: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  if (!status) return null;
  const colors = STATUS_COLORS[status] ?? { bg: '#6b7280', text: '#fff' };
  return (
    <span style={{
      display: 'inline-block', fontSize: '0.65rem', fontWeight: 600,
      padding: '2px 8px', borderRadius: 4, backgroundColor: colors.bg, color: colors.text,
      letterSpacing: '0.03em', lineHeight: '1.4',
    }}>
      {status}
    </span>
  );
};

/* ── Match dot (comparison table) ─────────────────────────────────── */

const MatchDot: React.FC<{ match: boolean }> = ({ match }) => (
  <span style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    backgroundColor: match ? '#22c55e' : '#d1d5db', marginRight: 6, verticalAlign: 'middle',
  }} />
);

/* ── Reason tags shown on each card for instant clarity ───────────── */

const REASON_ICONS: Record<string, string> = {
  'same activity': '🔗',
  'close GPS': '📍',
  'within date window': '📅',
  'similar comments': '💬',
  'similar description': '📝',
};

function getReasonIcon(reason: string): string {
  for (const [key, icon] of Object.entries(REASON_ICONS)) {
    if (reason.toLowerCase().startsWith(key)) return icon;
  }
  return '•';
}

const ReasonBadges: React.FC<{ reasonSummary: string }> = ({ reasonSummary }) => {
  const reasons = reasonSummary.split(', ').filter(Boolean);
  if (reasons.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
      {reasons.map((r, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          fontSize: '0.7rem', padding: '1px 7px', borderRadius: 3,
          backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
          fontWeight: 500, lineHeight: '1.5',
        }}>
          {getReasonIcon(r)} {r.replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      ))}
    </div>
  );
};

/* ── Main component ───────────────────────────────────────────────── */

const WorkIdMergeSuggestionsModal: React.FC<WorkIdMergeSuggestionsModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess
}) => {
  const [data, setData] = useState<MergeSuggestionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [rejectedIds, setRejectedIds] = useState<Set<number>>(new Set());
  const [rejectedCandidates, setRejectedCandidates] = useState<MergeSuggestionCandidate[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedDetailTab, setExpandedDetailTab] = useState<'details' | 'photos'>('details');
  const [mergeNote, setMergeNote] = useState('');
  const [showMergeNote, setShowMergeNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  /** Attachments for current work ID (loaded when Photos tab is used). */
  const [currentWorkIdAttachments, setCurrentWorkIdAttachments] = useState<WorkIdAttachment[] | null>(null);
  /** Attachments per candidate workIdId (loaded when Photos tab is used for that candidate). */
  const [candidateAttachmentsMap, setCandidateAttachmentsMap] = useState<Record<number, WorkIdAttachment[]>>({});
  const [loadingPhotosFor, setLoadingPhotosFor] = useState<number | null>(null);

  const group = data?.suggestions?.[0];
  const candidates = group?.candidates ?? [];
  const sourceLocation = data?.sourceLocation ?? null;

  const fetchSuggestions = useCallback(async () => {
    if (!workId?.id || !visible) return;
    setLoading(true);
    setError(null);
    try {
      const res = await workIdService.getMergeSuggestions(workId.id);
      setData(res);
      const firstGroup = res.suggestions?.[0];
      const defaultParentId = firstGroup?.defaultParentWorkIdId ?? null;
      // Pre-select the default parent only when it's a candidate (not the current work ID); we always merge to the oldest.
      setSelectedIds(
        defaultParentId != null && defaultParentId !== workId.id
          ? new Set([defaultParentId])
          : new Set()
      );
      setRejectedIds(new Set());
      setRejectedCandidates([]);
      setExpandedId(null);
      setMergeNote('');
      setShowMergeNote(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [workId?.id, visible]);

  useEffect(() => {
    if (visible && workId?.id) fetchSuggestions();
  }, [visible, workId?.id, fetchSuggestions]);

  useEffect(() => {
    setExpandedDetailTab('details');
  }, [expandedId]);

  useEffect(() => {
    if (!visible) {
      setCurrentWorkIdAttachments(null);
      setCandidateAttachmentsMap({});
    }
  }, [visible]);

  /** Load attachments for current work ID and expanded candidate when Photos tab is active. */
  useEffect(() => {
    if (!workId?.id || expandedDetailTab !== 'photos' || expandedId == null) return;
    const candidate = candidates.find((c) => c.workIdId === expandedId);
    if (!candidate) return;

    let cancelled = false;
    setLoadingPhotosFor(expandedId);

    const load = async () => {
      try {
        const needCurrent = currentWorkIdAttachments === null;
        const needCandidate = !candidateAttachmentsMap[candidate.workIdId];
        const [currentDetail, candidateDetail] = await Promise.all([
          needCurrent ? workIdService.getById(workId.id) : Promise.resolve(null),
          needCandidate ? workIdService.getById(candidate.workIdId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (needCurrent && currentDetail) setCurrentWorkIdAttachments(currentDetail.attachments ?? []);
        if (needCandidate && candidateDetail)
          setCandidateAttachmentsMap((prev) => ({
            ...prev,
            [candidate.workIdId]: candidateDetail.attachments ?? [],
          }));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load photos');
      } finally {
        if (!cancelled) setLoadingPhotosFor(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [workId?.id, expandedId, expandedDetailTab, candidates, currentWorkIdAttachments, candidateAttachmentsMap]);

  /* ── Selection helpers ──────────────────────────────────────────── */

  const activeCandidates = candidates.filter(c => !rejectedIds.has(c.workIdId));

  /**
   * Parent for merge:
   * - If any participant (current work ID or selected candidates) has at least one task, prefer those.
   * - Within the preferred set, pick the oldest by reportedOn.
   * - Otherwise, fall back to oldest by reportedOn overall.
   */
  const effectiveParentId = useMemo(() => {
    const participants: Array<{ id: number; reportedOn: string; hasTasks: boolean }> = [];

    if (workId) {
      const hasTasks = workId.task != null;
      participants.push({ id: workId.id, reportedOn: workId.reportedOn, hasTasks });
    }

    for (const c of activeCandidates) {
      if (selectedIds.has(c.workIdId)) {
        const hasTasks = c.task != null;
        participants.push({ id: c.workIdId, reportedOn: c.reportedOn, hasTasks });
      }
    }

    if (participants.length === 0) return null;

    const withTasks = participants.filter((p) => p.hasTasks);
    const pool = withTasks.length > 0 ? withTasks : participants;

    pool.sort((a, b) => new Date(a.reportedOn).getTime() - new Date(b.reportedOn).getTime());
    return pool[0].id;
  }, [selectedIds, activeCandidates, workId]);

  const toggleSelected = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allIds = activeCandidates.map(c => c.workIdId);
    const allSelected = allIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const allFilteredSelected = activeCandidates.length > 0 &&
    activeCandidates.every(c => selectedIds.has(c.workIdId));

  /* ── Merge handler ──────────────────────────────────────────────── */

  const handleMerge = async () => {
    if (!workId || effectiveParentId == null) return;
    const parentId = effectiveParentId;
    const hasWorkToDo = workId.id !== parentId || selectedIds.size > 0;
    if (!hasWorkToDo) return;
    setSubmitting(true);
    setError(null);
    try {
      const comments = mergeNote.trim() || undefined;
      const merges: Array<{ childWorkIdId: number; parentWorkIdId: number; comments?: string }> = [];

      if (workId.id !== parentId) {
        merges.push({ childWorkIdId: workId.id, parentWorkIdId: parentId, comments });
      }

      selectedIds.forEach(candidateId => {
        if (candidateId !== parentId && candidateId !== workId.id) {
          merges.push({ childWorkIdId: candidateId, parentWorkIdId: parentId, comments });
        }
      });

      if (merges.length === 0) return;
      if (merges.length === 1) {
        await workIdService.merge(merges[0].childWorkIdId, { targetWorkIdId: parentId, comments });
      } else if (merges.length > 1) {
        const result = await workIdService.bulkMerge(merges);
        if (result.errors.length > 0) {
          const errorSummary = result.errors.map(e => `#${e.childWorkIdId}: ${e.error}`).join('; ');
          setError(`Merged ${result.merged}, but ${result.errors.length} failed: ${errorSummary}`);
          if (result.merged > 0) onSuccess();
          return;
        }
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Reject handler ─────────────────────────────────────────────── */

  const handleReject = async (candidateId: number) => {
    if (!workId) return;
    const candidate = candidates.find(c => c.workIdId === candidateId);
    const label = candidate?.workIdentificationId || `#${candidateId}`;
    const confirmed = (await confirmAction({ title: 'Reject Merge Suggestion?', body: `The suggestion for "${label}" is removed from the list. This cannot be undone.`, confirmLabel: 'Reject', destructive: true }));
    if (!confirmed) return;
    setRejecting(true);
    setError(null);
    try {
      await workIdService.rejectMergeSuggestion(workId.id, candidateId);
      if (candidate) setRejectedCandidates(prev => [...prev, candidate]);
      setRejectedIds(prev => new Set(prev).add(candidateId));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(candidateId); return n; });
      if (expandedId === candidateId) setExpandedId(null);
      onSuccess();
      if (activeCandidates.length <= 1) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject suggestion');
    } finally {
      setRejecting(false);
    }
  };

  /* ── Comparison table for expanded card ─────────────────────────── */

  const renderComparisonTable = (candidate: MergeSuggestionCandidate) => {
    if (!workId) return null;
    const candidateLocation = candidate.location ?? null;
    const hasLocations = sourceLocation != null || candidateLocation != null;
    const gpsDist = (sourceLocation && candidateLocation)
      ? haversineMetres(sourceLocation.lat, sourceLocation.lng, candidateLocation.lat, candidateLocation.lng)
      : null;

    const currentDateStr = formatDate(workId.reportedOn);
    const candidateDateStr = formatDate(candidate.reportedOn);
    const datesMatch = currentDateStr === candidateDateStr;

    const currentReportedBy = formatReportedBy(workId.reportedBy);
    const candidateReportedBy = candidate.reportedBy || '–';
    const reportersMatch = currentReportedBy.toLowerCase() === candidateReportedBy.toLowerCase();

    const currentSummary = workId.comments || '–';
    const candidateSummary = candidate.summary || '–';
    const summaryMatch = currentSummary.toLowerCase() === candidateSummary.toLowerCase();

    const tdStyle: React.CSSProperties = {
      padding: '0.5rem 0.75rem', fontSize: '0.85rem', borderBottom: '1px solid #f3f4f6',
      verticalAlign: 'middle',
    };
    const labelStyle: React.CSSProperties = {
      ...tdStyle, fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', letterSpacing: '0.03em', width: '15%',
      backgroundColor: '#f9fafb',
    };

    return (
      <div style={{ padding: '0 1rem 1rem' }}>
        {hasLocations && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ position: 'relative', borderRadius: 4, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <div style={{ height: 200 }}>
                <GoogleGPSMap
                  startLocation={sourceLocation ?? candidateLocation ?? undefined}
                  endLocation={candidateLocation ?? sourceLocation ?? undefined}
                  onLocationSelect={() => {}}
                  locationType="start"
                  isPointActivity={!sourceLocation || !candidateLocation}
                  readOnly
                  autoFitBounds
                  hideInstructions
                  height={200}
                />
              </div>
              {gpsDist != null && (
                <div style={{
                  position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                  backgroundColor: '#1f2937', color: '#fff', padding: '4px 12px',
                  borderRadius: 16, fontSize: '0.75rem', fontWeight: 500, display: 'flex',
                  alignItems: 'center', gap: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                  </svg>
                  {formatDistance(gpsDist)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Shared Table primitive so this modal's header renders bold/uppercase like every
            other table in the app — the "some table headers are bold and some are not" report
            traced to this modal's own thStyle overriding the header instead of inheriting it. */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
          <Table size="sm" className="tw-mb-0">
            <thead>
              <tr>
                <th style={{ width: '15%' }}>Field</th>
                <th>
                  {workId.workIdentificationId || `#${workId.id}`}
                  <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>(CURRENT)</span>
                </th>
                <th>
                  {candidate.workIdentificationId || `#${candidate.workIdId}`}
                  <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>(merge suggestion)</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={labelStyle}>Reported On</td>
                <td style={tdStyle}><MatchDot match={datesMatch} />{currentDateStr}</td>
                <td style={tdStyle}><MatchDot match={datesMatch} />{candidateDateStr}</td>
              </tr>
              <tr>
                <td style={labelStyle}>Reported By</td>
                <td style={tdStyle}><MatchDot match={reportersMatch} />{currentReportedBy}</td>
                <td style={tdStyle}><MatchDot match={reportersMatch} />{candidateReportedBy}</td>
              </tr>
              <tr>
                <td style={labelStyle}>Summary</td>
                <td style={tdStyle}><MatchDot match={summaryMatch} />{currentSummary}</td>
                <td style={tdStyle}><MatchDot match={summaryMatch} />{candidateSummary}</td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  /** Photos tab: side-by-side CURRENT vs PARENT attachments. */
  const renderPhotosComparison = (candidate: MergeSuggestionCandidate) => {
    if (!workId) return null;
    const currentAttachments = currentWorkIdAttachments ?? [];
    const parentAttachments = candidateAttachmentsMap[candidate.workIdId] ?? [];
    const loading = loadingPhotosFor === candidate.workIdId;

    const isImage = (att: WorkIdAttachment) =>
      att.attachmentType === 'image' ||
      (att.attachmentName || '').toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/);
    const currentImages = currentAttachments.filter(isImage);
    const parentImages = parentAttachments.filter(isImage);

    const panelStyle: React.CSSProperties = {
      flex: 1,
      minWidth: 0,
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: 4,
      backgroundColor: '#fff',
    };
    const headerStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginBottom: 8,
      fontSize: '0.85rem',
      fontWeight: 600,
      color: '#374151',
    };
    const countPillStyle: React.CSSProperties = {
      fontSize: '0.7rem',
      padding: '2px 8px',
      borderRadius: 12,
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      fontWeight: 500,
    };

    return (
      <div style={{ padding: '0 1rem 1rem' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden />
            Loading photos…
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={panelStyle}>
              <div style={headerStyle}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                {workId.workIdentificationId || `#${workId.id}`}
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>(CURRENT)</span>
              </div>
              <div style={countPillStyle}>
                {currentImages.length} photo{currentImages.length !== 1 ? 's' : ''}
              </div>
              <div style={{ marginTop: 8 }}>
                <WorkIdImageAttachments workIdId={workId.id} attachments={currentAttachments} showHeading={false} />
              </div>
            </div>
            <div style={panelStyle}>
              <div style={headerStyle}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                {candidate.workIdentificationId || `#${candidate.workIdId}`}
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>(merge suggestion)</span>
              </div>
              <div style={countPillStyle}>
                {parentImages.length} photo{parentImages.length !== 1 ? 's' : ''}
              </div>
              <div style={{ marginTop: 8 }}>
                <WorkIdImageAttachments workIdId={candidate.workIdId} attachments={parentAttachments} showHeading={false} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ── Render card for each suggestion ────────────────────────────── */

  const renderSuggestionCard = (c: MergeSuggestionCandidate) => {
    const pct = Math.round((c.score ?? 0) * 100);
    const isParent = c.workIdId === effectiveParentId;
    const isExpanded = expandedId === c.workIdId;
    const isSelected = selectedIds.has(c.workIdId);
    const isRejected = rejectedIds.has(c.workIdId);
    const hasTasks = c.task != null;
    const summaryLine = buildSummaryLine(c);

    return (
      <div
        key={c.workIdId}
        style={{
          border: `1px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
          borderRadius: 4, marginBottom: 10, backgroundColor: '#f0f0f0',
          boxShadow: isSelected ? '0 0 0 2px rgba(59,130,246,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
          opacity: isRejected ? 0.5 : 1, transition: 'all 0.15s ease',
        }}
      >
        {/* Card header row */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '0.75rem 1rem', gap: '0.75rem',
        }}>
          {/* Checkbox */}
          {!isRejected && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelected(c.workIdId)}
              style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0, accentColor: '#3b82f6' }}
            />
          )}

          {/* ID, badges, summary, reason tags */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {c.workIdentificationId || `#${c.workIdId}`}
              </span>
              {isParent && (
                <span style={{
                  display: 'inline-block',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 3,
                  backgroundColor: '#1f2937',
                  color: '#fff',
                  letterSpacing: '0.05em',
                }}>
                  {hasTasks ? 'Parent (has task)' : 'Parent'}
                </span>
              )}
              {hasTasks && !isParent && (
                <span style={{
                  display: 'inline-block',
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  padding: '2px 6px',
                  borderRadius: 3,
                  backgroundColor: '#f97316',
                  color: '#111827',
                  letterSpacing: '0.05em',
                }}>
                  Has task assigned
                </span>
              )}
              <StatusBadge status={c.status} />
            </div>
            {summaryLine && (
              <div style={{
                fontSize: '0.8rem', color: '#6b7280', marginTop: 2,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {summaryLine}
              </div>
            )}
            <ReasonBadges reasonSummary={c.reasonSummary} />
          </div>

          {/* Match ring */}
          <MatchRing pct={pct} />

          {/* Reject button */}
          {!isRejected && (
            <button
              onClick={(e) => { e.stopPropagation(); handleReject(c.workIdId); }}
              disabled={rejecting}
              style={{
                border: '1px solid #fca5a5', color: '#ef4444', backgroundColor: 'transparent',
                borderRadius: 4, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 500,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              Reject
            </button>
          )}

          <button
            onClick={() => setExpandedId(isExpanded ? null : c.workIdId)}
            style={{
              border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', borderRadius: 4,
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0, transition: 'transform 0.15s',
            }}
          >
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>

        {/* Expanded detail: Details vs Photos tabs */}
        {isExpanded && (
          <div style={{ borderTop: '1px solid #e5e7eb' }}>
            <Nav
              variant="tabs"
              activeKey={expandedDetailTab}
              onSelect={(k) => setExpandedDetailTab((k as 'details' | 'photos') || 'details')}
              style={{
                paddingLeft: '1rem',
                paddingRight: '1rem',
                paddingTop: 4,
                gap: 0,
                borderBottom: 'none',
                backgroundColor: '#f9fafb',
              }}
            >
              <Nav.Item>
                <Nav.Link
                  eventKey="details"
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: expandedDetailTab === 'details' ? '#111827' : '#6b7280',
                    borderColor: expandedDetailTab === 'details' ? '#fff #e5e7eb #fff' : 'transparent',
                    backgroundColor: expandedDetailTab === 'details' ? '#fff' : 'transparent',
                  }}
                >
                  Details
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="photos"
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: expandedDetailTab === 'photos' ? '#111827' : '#6b7280',
                    borderColor: expandedDetailTab === 'photos' ? '#fff #e5e7eb #fff' : 'transparent',
                    backgroundColor: expandedDetailTab === 'photos' ? '#fff' : 'transparent',
                  }}
                >
                  Photos
                </Nav.Link>
              </Nav.Item>
            </Nav>
            {expandedDetailTab === 'details' && renderComparisonTable(c)}
            {expandedDetailTab === 'photos' && renderPhotosComparison(c)}
          </div>
        )}
      </div>
    );
  };

  /* ── Modal render ───────────────────────────────────────────────── */

  if (!visible) return null;

  const selectedCount = selectedIds.size;
  const canMerge =
    effectiveParentId != null &&
    (workId?.id !== effectiveParentId || selectedCount > 0);

  // The sections below were already built as a flex column (flexShrink:0 header/footer,
  // flex:1 + overflowY:auto body), so they drop straight into the Modal panel unchanged —
  // keeping this 900-line component's bespoke header styling intact while gaining the focus
  // trap, ESC, scroll lock, centering, standardized height and mobile sheet.
  return (
    <Modal show={visible} onHide={onClose} size="xl">

        {/* ─── Header ──────────────────────────────────────────── */}
        <div style={{
          backgroundColor: '#f8f9fa', padding: '1.25rem 1.5rem', flexShrink: 0,
          borderBottom: '1px solid #dee2e6',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{
                fontSize: '0.65rem', fontWeight: 600,
                letterSpacing: '0.12em', color: '#6b7280', marginBottom: 4,
              }}>
                Duplicate Detection
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: '1.4rem', color: '#111827' }}>Merge Suggestions</h4>
                {workId?.workIdentificationId && (
                  <span style={{
                    border: '1px solid #d1d5db', borderRadius: 3, backgroundColor: '#fff',
                    padding: '3px 10px', fontSize: '0.8rem', fontWeight: 500, color: '#374151',
                  }}>
                    {workId.workIdentificationId}
                  </span>
                )}
                {workId && effectiveParentId === workId.id && (
                  <span style={{
                    display: 'inline-block',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 3,
                    backgroundColor: '#1f2937',
                    color: '#fff',
                    letterSpacing: '0.05em',
                  }}>
                    {workId.task != null
                      ? 'Parent (has task)'
                      : 'Parent (oldest)'}
                  </span>
                )}
              </div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.5 }}>
                {activeCandidates.length} potential duplicate{activeCandidates.length !== 1 ? 's' : ''} found.
                Select the ones you want to merge, then click{' '}
                <strong style={{ color: '#111827' }}>Merge selected</strong>.
                The oldest work ID is automatically designated as the parent.
              </p>
              {workId && effectiveParentId === workId.id && activeCandidates.length > 0 && (
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#166534', fontWeight: 500, lineHeight: 1.4 }}>
                  {workId.task != null
                    ? 'This work ID already has a task assigned, so selected suggestions will merge into it.'
                    : 'This work ID is the parent – selected suggestions will merge into it.'}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              type="button"
              aria-label="Close"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>

        {/* ─── Selection bar ────────────────────────────────────── */}
        {!loading && candidates.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '0.625rem 1.5rem', borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb', flexShrink: 0, fontSize: '0.85rem',
          }}>
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleSelectAll}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#3b82f6' }}
            />
            <span style={{ color: '#6b7280' }}>
              <strong style={{ color: '#111827' }}>{selectedCount} selected</strong>{' '}
              of {activeCandidates.length} suggestion{activeCandidates.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* ─── Body: scrollable suggestion cards ───────────────── */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '1rem 1.5rem',
          WebkitOverflowScrolling: 'touch',
        }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '3rem 0', color: '#6b7280' }}>
              <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden />
              Loading suggestions…
            </div>
          )}

          {error && (
            <Alert variant="danger" className="tw-py-2 tw-mb-3" role="alert">{error}</Alert>
          )}

          {!loading && data && activeCandidates.length === 0 && (
            <div style={{
              textAlign: 'center', padding: '2rem 0', color: '#9ca3af', fontSize: '0.9rem',
            }}>
              No merge suggestions for this Work ID.
            </div>
          )}

          {!loading && activeCandidates.map(c => renderSuggestionCard(c))}
        </div>

        {/* ─── Footer ──────────────────────────────────────────── */}
        <div style={{ borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          {/* Merge note (collapsible) */}
          <div style={{ padding: '0.5rem 1.5rem 0' }}>
            <button
              onClick={() => setShowMergeNote(!showMergeNote)}
              style={{
                background: 'none', border: 'none', padding: 0, fontSize: '0.8rem',
                color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <svg
                width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: showMergeNote ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              Add a merge note <span style={{ color: '#9ca3af' }}>(optional)</span>
            </button>
            {showMergeNote && (
              <Form.Control
                as="textarea"
                rows={2}
                value={mergeNote}
                onChange={e => setMergeNote(e.target.value)}
                placeholder="Add a note about this merge…"
                style={{ marginTop: 8, fontSize: '0.85rem' }}
              />
            )}
          </div>

          {/* Action bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0.75rem 1.5rem',
          }}>
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              {selectedCount === 0
                ? 'Select suggestions above to merge'
                : (() => {
                    const isCurrentParent = effectiveParentId === workId?.id;
                    const parentLabel = isCurrentParent
                      ? workId?.workIdentificationId || `#${workId?.id}`
                      : activeCandidates.find(c => c.workIdId === effectiveParentId)?.workIdentificationId || `#${effectiveParentId}`;
                    if (isCurrentParent) {
                      return `Current work ID is the parent – merging ${selectedCount} suggestion${selectedCount !== 1 ? 's' : ''} into it`;
                    }
                    return `Merging into ${parentLabel} (parent)`;
                  })()}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleMerge}
                disabled={!canMerge || submitting || rejecting}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  opacity: canMerge ? 1 : 0.6,
                }}
              >
                {submitting ? 'Merging…' : 'Merge selected'}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: '50%', fontSize: '0.75rem', fontWeight: 700,
                  backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff',
                }}>
                  {selectedCount}
                </span>
              </Button>
            </div>
          </div>
        </div>
    </Modal>
  );
};

export default WorkIdMergeSuggestionsModal;
